<template>
  <full-screen-backdrop v-show="show" @click.native.self="close($event)">
    <div
      class="dropdown-mobile-options-container"
      ref="dropdown"
      :class="{ open: show, close: !show, searchable }"
      tabindex="-1"
      @scroll.capture="blurSearchInputOnScroll"
    >
      <div class="options-heading -pxy_2">
        <form
          v-if="searchable"
          class="search-input"
          :class="{ filled: searchValue }"
          name="search language"
          @submit.prevent="blurOnKeySearchPressed">
          <base-input
            v-model="searchValue"
            ref="optionsSearch"
            type="text"
            :placeholder="searchPlaceholder"
          />

          <sword-icon-wuk
            class="search-icon -ml_3"
            name="search"
            width="20px"
            height="20px"
          ></sword-icon-wuk>
        </form>
        <p v-else class="options-heading__title">{{ heading }}</p>
        <sword-icon-wuk class="close-icon -ml_2"
                        name="close"
                        @click.native.stop="close($event)">
        </sword-icon-wuk>
      </div>
      <div v-if="searchable && searchValue && !optionsObject.options.length" class="no-results t3">
        {{ noResultsLabel }} <b>{{ searchValue }}</b>
      </div>
      <options-list-wuk class="scrollable"
                        :value="value"
                        :options="optionsObject.options"
                        :item-align="itemAlign"
                        @click="handleSelection"
                        @linkClick="close($event)" />
    </div>
  </full-screen-backdrop>
</template>

<script>
import { nextTick } from 'vue';
import { useThrottleFn } from '@vueuse/core';
import { toggleBodyOverflow, unlockBodyOverflow, isSmallScreen } from '@ui-kit/scripts/utils/body-overflow';

import dropdownUtils from '@ui-kit/mixins/inputs/dropdowns';

import inputHandler from '@ui-kit/mixins/inputHandler';
import inputValidationTriggers from '@ui-kit/mixins/inputValidationTriggers';

import BaseInput from '@ui-kit/components/inputs/BaseInput.vue';
import FullScreenBackdrop from '@ui-kit/components/FullScreenBackdrop.vue';

import SwordIconWuk from '@ui-kit/components/icons/SwordIcon.vue';
import OptionsListWuk from '../../menus/OptionsList.vue';

export default {
  name: 'mobileOptionsListWuk',
  mixins: [dropdownUtils, inputHandler, inputValidationTriggers],
  components: {
    FullScreenBackdrop,
    SwordIconWuk,
    BaseInput,
    OptionsListWuk,
  },
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    heading: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      required: false,
    },
    itemAlign: {
      type: String,
      validator: value => ['center', 'start', 'end'].includes(value),
    }
  },
  computed: {
    optionsHaveIcon() {
      return this.options.some(op => op.optIcon);
    },
  },
  methods: {
    itemClass(option) {
      return {
        selected: this.selectedOpt && option.optValue === this.selectedOpt.optValue,
        disabled: option.disabled,
      };
    },
    handleSelection(option) {
      this.selectOption(option);
      this.close();
    },
    blurSearchInputOnScroll: useThrottleFn(function blurSearchInput() {
      if (isSmallScreen() && this.$refs.optionsSearch) {
        this.$refs.optionsSearch.$el.blur();
      }
    }, 200),
    blurOnKeySearchPressed($event) {
      if ($event.type === 'submit') {
        $event.preventDefault();
        this.$refs.optionsSearch.$el.blur();
      }
    }
  },
  watch: {
    show: {
      handler(isOpen) {
        if (isOpen) {
          nextTick(() => {
            if (this.searchable) this.$refs.optionsSearch.$el.focus();
          });
        }

        nextTick(() => {
          toggleBodyOverflow(isOpen, { justBlockIfIsSmallScreen: true });
        });
      },
      flush: 'post',
    }
  },
  beforeDestroy() {
    unlockBodyOverflow();
  }
};
</script>

<style lang="scss" scoped>
  @import "@ui-kit/styles/animations/_slide-in-out.scss";

  .dropdown-mobile-options-container {
    background-color: $dropdown-mobile-options-container-background;
    position: fixed;
    width: 100%;
    bottom: 0;
    left: 0;
    border-radius: $dropdown-mobile-options-container-top-radius-wuk $dropdown-mobile-options-container-top-radius-wuk 0 0;
    max-height: $dropdown-mobile-options-max-height-wuk;
    display: flex;
    flex-direction: column;
    outline: none;
    z-index: 26;
    padding-bottom: 1rem;

    &.open {
      animation: slide-from-bottom 0.3s linear forwards;
    }

    &.close {
      animation: slide-to-bottom 0.3s linear forwards;
    }

    &.searchable {
      // It's necessary for mobile keyboard don't hide searchable list
      height: $dropdown-mobile-options-max-height-wuk;
    }
  }

  .options-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .close-icon {
      width: 1.5rem;
      height: 1.5rem;
      cursor: pointer;
    }

    &__title {
      font-size: 1.125rem;
      font-weight: 600;
      color: $context-color-1;
      margin: 0.75rem 0;
    }
  }

  .options-list-item {
    padding: 0.75rem 1rem !important;
  }

  .scrollable {
    overflow: auto;
    @include invisible-scrollbar-wuk;
  }

  .search-input {
    flex: 1;
    border: 1px solid $dropdown-mobile-search-border-color;
    border-radius: 1rem;
    height: 3rem;
    display: flex;
    background: $background-color-normal;
    color: $input-default-text-color-wuk;
    align-items: center;
    overflow: hidden;

    input {
      background: transparent;
    }

    .search-icon {
      order: 1;
      flex: none;
      color: $context-color-1;
    }

    &:hover, &:focus-within, &.filled {
      color: $input-filled-text-color-wuk;
      border-color: $input-filled-text-color-wuk;
    }

    input {
      padding-top: 0;
      padding-bottom: 0;
      order: 2;
      flex: 1;
    }
  }

  .no-results {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    gap: .4rem;
    color: $context-color-3;
    padding: 1rem;
  }

</style>
